import { useState, useCallback, useEffect } from 'react';
import { useToast } from '@chakra-ui/react';
import { fetchWithAuth } from '../../utils/fetchWithAuth';

/**
 * Admin data access for workspaces, memberships and metadata.
 *
 * THE BOUNDARY THIS RESPECTS (Steph's rule, 2026-08-03)
 * -----------------------------------------------------
 * An admin manages workspaces and users, and has NO access to conversation or
 * document CONTENT. To read a workspace they create a user with access to it,
 * and that access is a visible Membership row.
 *
 * Note what this hook cannot do, and why it is not a limitation to route
 * around: there is NO function here to open a conversation or a document,
 * because the API serves no such thing to an admin. The backend
 * (services/admin_workspace_service.py) never selects prompt, response,
 * original_text or edited_text -- so the UI cannot display content even by
 * accident. The boundary lives in the service, not in this file's restraint.
 *
 * If a future admin screen needs to show content, that is a backend
 * conversation first, not a fetch added here.
 */
export const useAdminWorkspaces = () => {
    const [workspaces, setWorkspaces] = useState([]);
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const toast = useToast();

    const reportError = useCallback((title, error) => {
        console.error(title, error);
        toast({
            title,
            description: error?.message || 'Unexpected error',
            status: 'error',
            duration: 5000,
            isClosable: true,
        });
    }, [toast]);

    const detailFrom = async (response, fallback) => {
        try {
            const data = await response.json();
            return data.detail || fallback;
        } catch (e) {
            return fallback;
        }
    };

    const fetchWorkspaces = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await fetchWithAuth('/api/admin/workspaces');
            if (!response.ok) {
                throw new Error(await detailFrom(response, 'Failed to load workspaces'));
            }
            const data = await response.json();
            setWorkspaces(data);
            return data;
        } catch (error) {
            reportError('Could not load workspaces', error);
            return [];
        } finally {
            setIsLoading(false);
        }
    }, [reportError]);

    // Users come from the PRE-EXISTING admin endpoint in auth.py. Reused rather
    // than duplicated: the admin surface for users already existed before the
    // workspace API was built.
    const fetchUsers = useCallback(async () => {
        try {
            const response = await fetchWithAuth('/api/auth/users');
            if (!response.ok) {
                throw new Error(await detailFrom(response, 'Failed to load users'));
            }
            const data = await response.json();
            setUsers(data);
            return data;
        } catch (error) {
            reportError('Could not load users', error);
            return [];
        }
    }, [reportError]);

    const createWorkspace = useCallback(async ({ slug, name }) => {
        const response = await fetchWithAuth('/api/admin/workspaces', {
            method: 'POST',
            body: { slug, name },
        });
        if (!response.ok) {
            throw new Error(await detailFrom(response, 'Failed to create workspace'));
        }
        const created = await response.json();
        await fetchWorkspaces();
        toast({
            title: 'Workspace created',
            // Says the quiet part out loud: creation grants nothing. The admin
            // has to add themselves as a member, visibly, to see anything in it.
            description: `${created.name} — no members yet`,
            status: 'success',
            duration: 4000,
            isClosable: true,
        });
        return created;
    }, [fetchWorkspaces, toast]);

    const updateWorkspace = useCallback(async (workspaceId, changes) => {
        const response = await fetchWithAuth(`/api/admin/workspaces/${workspaceId}`, {
            method: 'PATCH',
            body: changes,
        });
        if (!response.ok) {
            throw new Error(await detailFrom(response, 'Failed to update workspace'));
        }
        const updated = await response.json();
        await fetchWorkspaces();
        return updated;
    }, [fetchWorkspaces]);

    const deleteWorkspace = useCallback(async (workspaceId) => {
        const response = await fetchWithAuth(`/api/admin/workspaces/${workspaceId}`, {
            method: 'DELETE',
        });
        if (!response.ok) {
            // 409 here is the emptiness rule: an admin may not destroy content it
            // may not read. Surface the server's explanation verbatim rather than
            // a generic failure -- it tells the operator exactly what to do next.
            throw new Error(await detailFrom(response, 'Failed to delete workspace'));
        }
        await fetchWorkspaces();
    }, [fetchWorkspaces]);

    const fetchMembers = useCallback(async (workspaceId) => {
        try {
            const response = await fetchWithAuth(
                `/api/admin/workspaces/${workspaceId}/members`
            );
            if (!response.ok) {
                throw new Error(await detailFrom(response, 'Failed to load members'));
            }
            return await response.json();
        } catch (error) {
            reportError('Could not load members', error);
            return [];
        }
    }, [reportError]);

    const addMember = useCallback(async (workspaceId, { userId, role }) => {
        const response = await fetchWithAuth(
            `/api/admin/workspaces/${workspaceId}/members`,
            { method: 'POST', body: { user_id: userId, role } }
        );
        if (!response.ok) {
            throw new Error(await detailFrom(response, 'Failed to grant access'));
        }
        const membership = await response.json();
        await fetchWorkspaces();
        return membership;
    }, [fetchWorkspaces]);

    const updateMemberRole = useCallback(async (workspaceId, userId, role) => {
        const response = await fetchWithAuth(
            `/api/admin/workspaces/${workspaceId}/members/${userId}`,
            { method: 'PATCH', body: { role } }
        );
        if (!response.ok) {
            throw new Error(await detailFrom(response, 'Failed to change role'));
        }
        return await response.json();
    }, []);

    const removeMember = useCallback(async (workspaceId, userId) => {
        const response = await fetchWithAuth(
            `/api/admin/workspaces/${workspaceId}/members/${userId}`,
            { method: 'DELETE' }
        );
        if (!response.ok) {
            throw new Error(await detailFrom(response, 'Failed to revoke access'));
        }
        await fetchWorkspaces();
    }, [fetchWorkspaces]);

    // METADATA ONLY. These return filenames, sizes, counts and dates -- the
    // backend serves nothing else, by design.
    const fetchDocumentsMetadata = useCallback(async (workspaceId = null) => {
        try {
            const query = workspaceId != null ? `?workspace_id=${workspaceId}` : '';
            const response = await fetchWithAuth(`/api/admin/documents${query}`);
            if (!response.ok) {
                throw new Error(await detailFrom(response, 'Failed to load documents'));
            }
            return await response.json();
        } catch (error) {
            reportError('Could not load document metadata', error);
            return [];
        }
    }, [reportError]);

    const fetchConversationsMetadata = useCallback(async (workspaceId = null) => {
        try {
            const query = workspaceId != null ? `?workspace_id=${workspaceId}` : '';
            const response = await fetchWithAuth(`/api/admin/conversations${query}`);
            if (!response.ok) {
                throw new Error(
                    await detailFrom(response, 'Failed to load conversations')
                );
            }
            return await response.json();
        } catch (error) {
            reportError('Could not load conversation metadata', error);
            return [];
        }
    }, [reportError]);

    useEffect(() => {
        fetchWorkspaces();
        fetchUsers();
    }, [fetchWorkspaces, fetchUsers]);

    return {
        workspaces,
        users,
        isLoading,
        fetchWorkspaces,
        fetchUsers,
        createWorkspace,
        updateWorkspace,
        deleteWorkspace,
        fetchMembers,
        addMember,
        updateMemberRole,
        removeMember,
        fetchDocumentsMetadata,
        fetchConversationsMetadata,
    };
};
